import { useContext, useEffect } from 'react';
import { useVesselStore } from '../context/VesselContext';
import useGetVessel from '../api/useGetVessel';
import getContext from '../context/AppSettingsContext';
import useSignalRConnection from './useSignalRConnection';
import { HubConnectionState } from '@microsoft/signalr';
import useGetMasterPlanApi from 'src/api/stowage/useGetMasterPlanApi';
import { useLocation } from 'react-router-dom';
import useAppFeature from './useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import useGetTankersEventsForCargoes from 'src/api/useGetTankersEventsForCargoes';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { useEventStore } from 'src/context/EventContext';
import useUpdateMissingTasksIcon from './useUpdateMissingTasksIcon';
import useCargoTempLogApi from 'src/api/cargo-temp-log/useCargoTempLogApi';
import { getFormattedCargoNumber } from 'src/utils/stowage/cargoOperationUtils';
import { getCurrentPortCall } from 'src/utils/stowage/stowageRotationUtils';
import usePreloadVesselData from 'src/hooks/usePreloadVesselData';
import { useConnectionStore } from 'src/context/ConnectionContext';
import { OfflineLevelEnum } from 'src/enums/offlineLevelEnum';
import { useAppStatus } from 'src/context/AppStatusContext';

const SetDataComponent = () => {
    const appSettings = useContext(getContext());
    const location = useLocation();

    const { selectedVessel, selectedListVessel } = useVesselStore();
    const { loadedMasterStowagePlan } = useStowagePlanStore();
    const { cargoOperationsEvents } = useEventStore();
    const { hubConnection, offlineLevel } = useConnectionStore();
    const { apiUnavailable } = useAppStatus();

    const { setupSignalRConnection, stopSignalRConnection } = useSignalRConnection();
    const { getVessel } = useGetVessel();
    const { getMasterStowagePlanFromApi } = useGetMasterPlanApi();
    const { isFeatureEnabled } = useAppFeature();
    const { getEventListForCargoes } = useGetTankersEventsForCargoes();
    const { updateCargoTempLogMissingTasks } = useUpdateMissingTasksIcon();
    const { getCargoTemperatureConfigs } = useCargoTempLogApi();
    const { applyPreloadedDataIfMatch } = usePreloadVesselData();

    // Effect 1: Vessel data fetch — triggered by selectedListVessel (set by RouterContent)
    // This bridges the gap: selectedListVessel → getVessel() → sets selectedVessel
    useEffect(() => {
        if (selectedListVessel) {
            // Try to use preloaded vessel data first, only fetch if not available
            applyPreloadedDataIfMatch(selectedListVessel.imoNumber).then((applied) => {
                if (!applied) {
                    // Preloaded data doesn't match or isn't available, fetch normally
                    getVessel(selectedListVessel.imoNumber);
                }
            });
        }
    }, [selectedListVessel]);

    // Effect 2: SignalR connection lifecycle (vessel only)
    useEffect(() => {
        if (selectedVessel && (!hubConnection || hubConnection.state === HubConnectionState.Disconnected)) {
            setupSignalRConnection(appSettings.api + '', selectedVessel.imoNumber);
        }
        return () => {
            stopSignalRConnection();
        };
    }, [selectedVessel]);

    // Effect: Reconnect SignalR when connectivity is restored after dormant period
    useEffect(() => {
        if (
            selectedVessel &&
            offlineLevel === OfflineLevelEnum.Online &&
            (!hubConnection || hubConnection.state === HubConnectionState.Disconnected)
        ) {
            setupSignalRConnection(appSettings.api + '', selectedVessel.imoNumber);
        }
    }, [offlineLevel]);

    // Effect: Reconnect SignalR when API recovers (apiUnavailable transitions false)
    useEffect(() => {
        if (!apiUnavailable && selectedVessel && (!hubConnection || hubConnection.state === HubConnectionState.Disconnected)) {
            setupSignalRConnection(appSettings.api + '', selectedVessel.imoNumber);
        }
    }, [apiUnavailable]);

    // Effect 3: Stowage plan loading (depends on full vessel data + location)
    useEffect(() => {
        if (selectedVessel) {
            // Only fetch the master stowage plan if we're not on the stowage page
            // and the vessel is active (inactive vessels have no stowage plan to load)
            const currentPath = location.pathname;
            if (
                selectedVessel.isActive === true &&
                isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: selectedVessel.imoNumber, date: getDateNow() }) &&
                currentPath !== '/stowage'
            ) {
                getMasterStowagePlanFromApi(selectedVessel);
            }
        }
    }, [selectedVessel, location.pathname]);

    useEffect(() => {
        if (loadedMasterStowagePlan && loadedMasterStowagePlan.cargoes && loadedMasterStowagePlan.cargoes.length > 0) {
            const cargoIds = loadedMasterStowagePlan.cargoes
                .filter((cargo) => cargo.imosCargoId != null)
                .map((cargo) => cargo.imosCargoId.toString())
                .filter((cargoId) => !isNaN(Number(cargoId)));
            if (cargoIds.length > 0) {
                getEventListForCargoes(cargoIds);

                // Only load cargo temp configs for current and previous voyage
                const currentPortCall = getCurrentPortCall(loadedMasterStowagePlan);

                if (currentPortCall) {
                    const currentVoyage = Number(currentPortCall.voyageNumber);
                    const previousVoyage = currentVoyage - 1;

                    const relevantCargoes = loadedMasterStowagePlan.cargoes.filter((cargo) => Number(cargo.voyageNumber) >= previousVoyage);

                    if (selectedVessel && relevantCargoes.length > 0) {
                        getCargoTemperatureConfigs(
                            selectedVessel.imoNumber,
                            relevantCargoes.map((cargo) => getFormattedCargoNumber(cargo.voyageNumber, cargo.cargoNumber, false)).join(','),
                            false
                        );
                    }
                }
            }
        }
    }, [loadedMasterStowagePlan]);

    useEffect(() => {
        if (cargoOperationsEvents && cargoOperationsEvents.length > 0 && selectedVessel) {
            updateCargoTempLogMissingTasks(selectedVessel.imoNumber);
        }
    }, [cargoOperationsEvents]);

    return {};
};

export default SetDataComponent;
